import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Check, Play, Pause, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface NotificationSound {
  id: string;
  name: string;
  url: string;
  type: 'short' | 'long';
}

interface NotificationSoundSelectorProps {
  onSelect: (soundUrl: string) => void;
  currentSound: string;
}

// Using Mixkit sound files with direct URLs
const NOTIFICATION_SOUNDS: NotificationSound[] = [
  // Short notification sounds
  { 
    id: 'notification1', 
    name: 'Bell', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-bell-notification-933.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification2', 
    name: 'Chime', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-positive-notification-951.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification3', 
    name: 'Ding', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-software-interface-start-2574.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification4', 
    name: 'Alert', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-alert-quick-chime-766.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification5', 
    name: 'Message', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-message-pop-alert-2354.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification6', 
    name: 'Notification', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-notification-sound-2356.mp3', 
    type: 'short' 
  },
  { 
    id: 'notification7', 
    name: 'Soft', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-software-interface-remove-2576.mp3', 
    type: 'short' 
  },
  
  // Longer ringtones from Mixkit
  { 
    id: 'ringtone1', 
    name: 'Modern Digital', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-modern-digital-ring-tone-2978.mp3', 
    type: 'long' 
  },
  { 
    id: 'ringtone2', 
    name: 'Digital Alarm', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-digital-clock-digital-alarm-1013.mp3', 
    type: 'long' 
  },
  { 
    id: 'ringtone3', 
    name: 'Cartoon Phone', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-cartoon-telephone-ring-1301.mp3', 
    type: 'long' 
  },
  { 
    id: 'ringtone4', 
    name: 'Vintage Phone', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-vintage-telephone-ring-1142.mp3', 
    type: 'long' 
  },
  { 
    id: 'ringtone5', 
    name: 'Urgent Tone', 
    url: 'https://assets.mixkit.co/sfx/preview/mixkit-urgent-simple-tone-2977.mp3', 
    type: 'long' 
  },
  
  // Custom Marimba Ringtone
  {
    id: 'marimba',
    name: 'Marimba Ringtone',
    url: 'https://qfvdrthqushzkfjniiou.supabase.co/storage/v1/object/public/sounds//mixkit-marimba-ringtone-1359%20-%20Copy.mp3',
    type: 'long'
  },
  
  // No sound option
  { id: 'none', name: 'No Sound', url: '', type: 'short' }
];

const NotificationSoundSelector: React.FC<NotificationSoundSelectorProps> = ({ onSelect, currentSound }) => {
  const [selectedSound, setSelectedSound] = useState<string>(currentSound || NOTIFICATION_SOUNDS[0].url);
  const [playingSound, setPlayingSound] = useState<string | null>(null);
  const [volume, setVolume] = useState<number>(0.5);
  const [muted, setMuted] = useState<boolean>(false);
  const [showSelector, setShowSelector] = useState<boolean>(false);
  const [savedSound, setSavedSound] = useState<string | null>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [soundType, setSoundType] = useState<'short' | 'long'>('short');

  useEffect(() => {
    // Load saved sound preference from user settings
    const loadSavedSound = async () => {
      if (!savedSound) {
        try {
          const { data: userSettings, error } = await supabase
            .from('user_settings')
            .select('notification_sound')
            .maybeSingle();
          
          if (error) {
            console.error('Error loading notification sound preference:', error);
            return;
          }
          
          if (userSettings?.notification_sound) {
            setSelectedSound(userSettings.notification_sound);
            setSavedSound(userSettings.notification_sound);
          }
        } catch (error) {
          console.error('Error loading notification sound preference:', error);
        }
      }
    };

    loadSavedSound();
  }, [savedSound]);

  useEffect(() => {
    // Handle clicks outside the selector to close it
    const handleClickOutside = (event: MouseEvent) => {
      if (showSelector && !(event.target as Element).closest('.search-container')) {
        setShowSelector(false);
        // Stop any playing sound when closing the selector
        if (playingSound) {
          stopAllSounds();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      stopAllSounds();
    };
  }, [showSelector]);

  const stopAllSounds = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingSound(null);
  };

  const handleSoundSelect = (sound: NotificationSound) => {
    setSelectedSound(sound.url);
    onSelect(sound.url);
    setAudioError(null);
    
    // Save preference to user settings
    const savePreference = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { error } = await supabase
          .from('user_settings')
          .upsert({ 
            user_id: user.id,
            notification_sound: sound.url 
          });
        
        if (error) {
          console.error('Error saving notification sound preference:', error);
          return;
        }
        
        setSavedSound(sound.url);
      } catch (error) {
        console.error('Error saving notification sound preference:', error);
      }
    };
    
    savePreference();
  };

  const togglePlay = async (sound: NotificationSound) => {
    try {
      if (playingSound === sound.id) {
        stopAllSounds();
        return;
      }

      stopAllSounds();
      
      if (!sound.url) {
        return;
      }

      // Create new audio instance
      const audio = new Audio(sound.url);
      
      // Set up event handlers before setting the source
      audio.onerror = (e) => {
        console.error('Audio error:', e);
        setAudioError(`Could not load sound: ${sound.name}. Please try again or select a different sound.`);
        setPlayingSound(null);
      };

      audio.onended = () => {
        setPlayingSound(null);
      };

      // Configure audio
      audio.volume = muted ? 0 : volume;
      audioRef.current = audio;
      
      // Play the audio
      try {
        await audio.play();
        setPlayingSound(sound.id);
        setAudioError(null);
      } catch (error) {
        console.error('Error playing sound:', error);
        setAudioError(`Could not play sound: ${sound.name}. Please check your browser's autoplay settings.`);
        setPlayingSound(null);
      }
    } catch (error) {
      console.error('Audio system error:', error);
      setAudioError(`Audio system error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setPlayingSound(null);
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
    if (audioRef.current) {
      audioRef.current.volume = !muted ? 0 : volume;
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current && !muted) {
      audioRef.current.volume = newVolume;
    }
  };

  const filteredSounds = NOTIFICATION_SOUNDS.filter(sound => 
    sound.id === 'none' || sound.type === soundType
  );

  return (
    <div className="relative" ref={selectorRef}>
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
      >
        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        <span>Notification Sound</span>
      </button>

      {showSelector && (
        <div className="absolute z-10 mt-2 w-96 bg-white rounded-lg shadow-xl p-4 right-0">
          <h3 className="text-lg font-semibold mb-3">Select Notification Sound</h3>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={toggleMute}
                className="text-gray-600 hover:text-gray-800"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-full mx-3"
                disabled={muted}
              />
            </div>
          </div>

          <div className="mb-4">
            <div className="flex rounded-lg overflow-hidden">
              <button
                onClick={() => setSoundType('short')}
                className={`flex-1 py-2 px-4 ${
                  soundType === 'short' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Short Sounds
              </button>
              <button
                onClick={() => setSoundType('long')}
                className={`flex-1 py-2 px-4 ${
                  soundType === 'long' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Ringtones
              </button>
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {filteredSounds.map((sound) => (
              <div 
                key={sound.id}
                className={`flex items-center justify-between p-2 rounded-lg mb-1 ${
                  selectedSound === sound.url ? 'bg-purple-100' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  {selectedSound === sound.url && (
                    <Check className="w-4 h-4 text-purple-600 mr-2" />
                  )}
                  <span className={selectedSound === sound.url ? 'font-medium' : ''}>
                    {sound.name}
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  {sound.url && (
                    <button
                      onClick={() => togglePlay(sound)}
                      className="p-1 rounded-full hover:bg-gray-200"
                    >
                      {playingSound === sound.id ? (
                        <Pause className="w-4 h-4 text-gray-600" />
                      ) : (
                        <Play className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => handleSoundSelect(sound)}
                    className={`px-2 py-1 text-xs rounded ${
                      selectedSound === sound.url
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {selectedSound === sound.url ? 'Selected' : 'Select'}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {audioError && (
            <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-start">
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{audioError}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSoundSelector;